/**
 * AdminPanel.jsx
 * 
 * Composant principal d'administration permettant la gestion :
 * - des régions
 * - des villages
 * - des parcours
 * - des questions
 * - des jeux éphémères (transient)
 * 
 * @module AdminPanel
 */

import React, { useState, useEffect } from 'react';
import styles from './AdminPanel.module.scss';
import { DEBUG, PATH } from '../../utils/constants';

// Onglets
import RegionsTab from './RegionsTab/RegionsTab';
import VillageTab from './VillageTab/VillageTab';
import ParcoursTab from './ParcoursTab/ParcoursTab';
import IndependantGameTab from './IndependantGameTab/IndependantGameTab';
import TransientTab from './TransientTab/TransientTab';
import UsersTab from './UsersTab/UsersTab';
import ErrorTab from './ErrorTab/ErrorTab';
import Stats from '../Stats/Stats';

// APIS 
import { getAllRegions, createRegion, updateRegion, deleteRegion } from '../../utils/api/regionsAPI';
import { getAllVillages, createVillage, updateVillage, deleteVillage } from '../../utils/api/villagesAPI';
import { getAllParcoursByVillage, createParcours, updateParcours, deleteParcours, toggleParcours } from '../../utils/api/parcoursAPI';
import { getAllQuestionsByParcours, createQuestion, updateQuestion, deleteQuestion } from '../../utils/api/questionsAPI';


const debug = DEBUG;

/**
 * Composant AdminPanel
 * Interface d'administration complète pour gérer la hiérarchie du jeu.
 * @component
 * @returns {JSX.Element} L'interface administrateur
 */
function AdminPanel() {

  // ---------------------------------STATES--------------------------------------------
  // const [activeTab, setActiveTab] = useState('regions');

  const [mainTab, setMainTab] = useState('jeux');
  const [activeSubTab, setActiveSubTab] = useState('regions');
  const tabsByMain = {
    jeux: ['regions', 'independant', 'transient'],
    gestion: ['users'],
    statistiques: [],
  };


  const [regions, setRegions] = useState([]);
  const [villages, setVillages] = useState([]);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [parcours, setParcours] = useState([]);
  const [selectedParcours, setSelectedParcours] = useState(null);
  const [questions, setQuestions] = useState([]);

  // ---------------------------------EFFECTS--------------------------------------------
  /**
   * Récupère toutes les régions et villages au montage
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [fetchedRegions, fetchedVillages] = await Promise.all([
          getAllRegions(),
          getAllVillages(),
        ]);
        setRegions(fetchedRegions);
        setVillages(fetchedVillages);
      } catch (error) {
        if(debug) console.error('Erreur lors du chargement des données', error);
      }
    };
    fetchData();
  }, []);

  /**
   * Trie les régions par nom
   */
  useEffect(() => {
    setRegions(regions.sort((a, b) => a.nom.localeCompare(b.nom)));
  }, [regions]);


  // ---------------------------------EVENTS--------------------------------------------
  // Fonctions de création (région, village, parcours, etc.)

  // ---------------------- RegionTab -----------------------------
  /**
   * Crée une nouvelle région
   * @param {string} regionName 
   * @returns {Object|boolean} nouvelle région ou false
   */
  const handleCreateRegion = async (regionName) => {
    try {
      const newRegion = await createRegion(regionName); // Appel à l'API pour créer la région
  
      // Vérifier si la réponse contient bien une région (ajoute une vérification ici si nécessaire)
      if (newRegion && newRegion.id) {
        // Mettre à jour l'état localement
        newRegion.nom = regionName;
        setRegions((prev) => [...prev, newRegion]);
  
        // Retourner true si la création a réussi
        return newRegion;
      } else {
        throw new Error("La réponse ne contient pas une région valide.");
        // return false;
      }
    } catch (error) {
      if(debug) console.error("Erreur création de région :", error);
      return false;
    }
  };

  /**
   * Met à jour une région
   * @param {number} regionId 
   * @param {string} newName 
   * @returns {boolean}
   */
  const handleUpdateRegion = async (regionId, newName) => {
    try {
      await updateRegion(regionId, newName);
      setRegions(prev => 
        prev.map(region => 
          region.id === regionId ? { ...region, nom: newName } : region
        )
      );
      return true;
    } catch (error) {
      if(debug) console.error("Erreur mise à jour de région :", error);
      return false;
    }
  };

  /**
   * Supprime une région + villages associés
   * @param {number} regionId 
   * @returns {boolean}
   */
  const handleDeleteRegion = async (regionId) => {
    try {
      await deleteRegion(regionId);
      setRegions(prev => prev.filter(region => region.id !== regionId));
      // Supprimer également les villages associés de l'état local
      setVillages(prev => prev.filter(village => village.id_region !== regionId));
      return true;
    } catch (error) {
      if(debug) console.error("Erreur suppression de région :", error);
      return false;
    }
  };

  /**
   * Crée un village dans une région
   * @param {number} regionId 
   * @param {string} villageName 
   */
  const handleCreateVillage = async (regionId, villageName) => {
    try {
      const newId = await createVillage(regionId, villageName);
      if (newId.id) {
        const newVillage = {
            id: newId.id, // ID retourné par l'API
            nom: villageName, // Nom saisi par l'utilisateur
            id_region: regionId, // ID de la région sélectionnée
        };

        if(debug) console.log("Nouveau village créé :", newVillage);
        
        setVillages((prevVillages) => [...prevVillages, newVillage]);
    }
      
    } catch (error) {
      if(debug) console.error('Erreur création de village', error);
    }
  };

  /**
   * Met à jour un village
   * @param {number} villageId 
   * @param {string} newName 
   * @returns {boolean}
   */
  const handleUpdateVillage = async (villageId, newName) => {
    try {
      await updateVillage(villageId, newName);
      setVillages(prev => 
        prev.map(village => 
          village.id === villageId ? { ...village, nom: newName } : village
        )
      );
      
      // Mettre à jour aussi selectedVillage si nécessaire
      if (selectedVillage && selectedVillage.id === villageId) {
        setSelectedVillage(prev => ({ ...prev, nom: newName }));
      }
      
      return true;
    } catch (error) {
      if(debug) console.error("Erreur mise à jour de village :", error);
      return false;
    }
  };

  /**
   * Supprime un village
   * @param {number} villageId 
   * @returns {boolean}
   */
  const handleDeleteVillage = async (villageId) => {
    try {
      await deleteVillage(villageId);
      setVillages(prev => prev.filter(village => village.id !== villageId));
      
      // Si le village supprimé est le village sélectionné, retour à l'onglet régions
      if (selectedVillage && selectedVillage.id === villageId) {
        setSelectedVillage(null);
        setActiveSubTab('regions');
      }
      
      return true;
    } catch (error) {
      if(debug) console.error("Erreur suppression de village :", error);
      return false;
    }
  };

  /**
   * Sélectionne un village et récupère ses parcours
   * @param {Object} village 
   */
  // Charge l'écran de la gestion des parcours
  const handleVillageSelect = async (village) => {
    setSelectedVillage(village);
    const avalaibles = await getAllParcoursByVillage(village.id);
    avalaibles.length > 0 && setParcours(avalaibles);
    setActiveSubTab('village'); // Change l'onglet actif vers ParcoursTab
  };

  // ------------------------- VillageTab -----------------------------

  /**
   * Crée un parcours pour un village
   * @param {number} villageId 
   * @param {string} parcoursName 
   * @param {string} lang 
   */
  const handleCreateParcours = async (villageId, parcoursName, lang) => {
    try {
      const response = await createParcours(villageId, parcoursName, lang);
      if(debug) console.log(response);
      const newParcours = {
        id: response.id,
        nom: parcoursName,
        lang: lang,
        id_village: villageId
      };
      setParcours((prev) => [...prev, newParcours]); 
      return newParcours;
    } catch (error) {
      if(debug) console.error('Erreur création de parcours', error);
    }
  };

  /**
   * Met à jour un parcours
   * @param {number} parcoursId 
   * @param {string} newName 
   * @param {string} intro 
   * @param {string} outro 
   * @param {string} lang 
   * @returns {boolean}
   */
  const handleUpdateParcours = async (parcoursId, newName, intro, outro, lang) => {
    try {
      await updateParcours(parcoursId, newName, intro, outro, lang);
      setParcours(prev => 
        prev.map(parcours => 
          parcours.id === parcoursId ? { ...parcours, nom: newName, intro:intro, outro:outro, lang: lang } : parcours
        )
      );
      
      // Mettre à jour aussi selectedParcours si nécessaire
      if (selectedParcours && selectedParcours.id === parcoursId) {
        setSelectedParcours(prev => ({ ...prev, nom: newName }));
      }
      
      return true;
    } catch (error) {
      if(debug) console.error("Erreur mise à jour de parcours :", error);
      return false;
    }
  };

  /**
   * Active/désactive un parcours
   * @param {number} parcoursId 
   * @returns {boolean}
   */
  const handleToggleParcoursActive = async (id, active) => {
    try {
        await toggleParcours(id, active); // ta fonction API existante
        setParcours(prev => prev.map(p => 
          p.id === id ? { ...p, active: active } : p
      ));
    } catch (error) {
        console.error("Erreur toggle parcours:", error);
    }
  };

  /**
   * Supprime un parcours
   * @param {number} parcoursId 
   * @returns {boolean}
   */
  const handleDeleteParcours = async (parcoursId) => {
    try {
      const verif = await deleteParcours(parcoursId);
      if(debug) console.log(verif);
      setParcours(prev => prev.filter(parcours => parcours.id !== parcoursId));// uniquement si suppression ok !!!
      
      // Si le parcours supprimé est le parcours sélectionné, retour à l'onglet village
      if (selectedParcours && selectedParcours.id === parcoursId) {
        setSelectedParcours(null);
        setActiveSubTab('village');
      }
      
      return true;
    } catch (error) {
      if(debug) console.error("Erreur suppression de parcours :", error);
      return false;
    }
  };

  /**
   * Sélectionne un parcours et récupère ses questions
   * @param {Object} parcours 
   */
  // Charge l'écran de la gestion d'un seul parcours + liste des questions 
  const handleParcoursSelect = async (parcours) => {
    setSelectedParcours(parcours);

    try {
      const questions = await getAllQuestionsByParcours(parcours.id);
      if (Array.isArray(questions)) {
        setQuestions(questions);
      } else {
        // Si c’est une erreur mais sans throw (ex: 404 avec message), on vide la liste
        setQuestions([]);
      }
    } catch (error) {
      // Si ton API throw vraiment l'erreur, on gère ici
      if (error.response && error.response.status === 404) {
        if(debug) console.warn("Aucune question pour ce parcours");
        setQuestions([]);
      } else {
        if(debug) console.error("Erreur récupération des questions :", error);
      }
    }

    setActiveSubTab('parcours');
  };

  // ---------------------- ParcoursTab -----------------------------
  /**
   * Crée une nouvelle question dans un parcours
   * @param {number} parcoursId 
   * @param {Object} data 
   */
  const handleCreateQuestion = async (parcoursId, data) => {
    try {
      const newQuestionId = await createQuestion(parcoursId, data.libelle, data.image, data.type, data.helper, data.img_helper);
      if (newQuestionId && newQuestionId.id) {
        // Créer un nouvel objet question avec les données et l'ID retourné
        const newQuestion = {
          id: newQuestionId.id,
          libelle: data.libelle,
          type: data.type,
          helper: data.helper,
          img: newQuestionId.hasOwnProperty('img') ? newQuestionId.img : (data.image ? data.image.name : null),
          img_helper: newQuestionId.hasOwnProperty('img_helper') ? newQuestionId.img_helper : (data.img_helper ? data.img_helper.name : null),
          id_parcours: parcoursId,
          placement: questions.length + 2
        };
        
        // Mettre à jour l'état local des questions
        setQuestions(prev => [...prev, newQuestion]);
      }
    } catch (error) {
      if(debug) console.error('Erreur création de question', error);
    }
  };

  /**
   * Met à jour une question
   * @param {number} questionId 
   * @param {Object} data 
   * @returns {boolean}
   */
  const handleUpdateQuestion = async (questionId, data) => {
    try {
      const result = await updateQuestion(questionId, data.libelle, data.image, data.type, data.helper, data.img_helper, data.delete_image);
      if (result.success) { // Vérifiez le format exact de la réponse de l'API
        // Mise à jour de l'état local des questions
        setQuestions(prev => prev.map(question => 
          question.id === questionId ? {
            ...question,
            libelle: data.libelle,
            type: data.type,
            helper: data.helper,
            ...(result.hasOwnProperty('img') ? { img: result.img } : {}), // ne modifie l'image que si l'API fournit une valeur
            ...(result.hasOwnProperty('img_helper') ? { img_helper: result.img_helper } : {}), // idem pour l'image d'indice fournie par l'API 
          } : question
        ));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur mise à jour de question', error);
      return false;
    }
  };

  /**
   * Supprime une question
   * @param {number} questionId 
   * @returns {boolean}
   */
  const handleDeleteQuestion = async (questionId, data) => {
    try {
      const result = await deleteQuestion(questionId);
      if (result) { // Vérifiez le format exact de la réponse de votre API
        // Supprimer la question de l'état local
        setQuestions(prev => prev.filter(question => question.id !== questionId));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur suppression de question', error);
      return false;
    }
  };

  // Nouvelle fonctions :
  const handleImageDelete = async (questionId) => {
    // try {
    //   const response = await fetch(`questions/${questionId}/image`, {
    //     method: 'DELETE',
    //   });
  
    //   if (!response.ok) {
    //     throw new Error('Erreur lors de la suppression de l\'image.');
    //   }
  
    //   // Mise à jour locale : enlever l'image
    //   setQuestions(prevQuestions =>
    //     prevQuestions.map(q =>
    //       q.id === questionId ? { ...q, img: null } : q
    //     )
    //   );
    // } catch (error) {
    //   console.error(error);
    //   throw error; // pour que ImageEditor puisse afficher une erreur
    // }
    if (debug) console.log("Fonction handleImageDelete à implémenter...")
  };

  /**
   * Change l’onglet actif
   * @param {string} tabName 
   */
  const handleTabChange = (tabName) => {
    setActiveSubTab(tabName);
  };

  const getSubTabLabel = (key) => ({
    regions: "Villages",
    independant: "Jeux insolites",
    transient: "Jeu éphémère",
    users: "Utilisateurs",
  }[key] || key);

  // ---------------------------------FONCTIONS--------------------------------------------
  /**
   * Retourne le contenu du tab actif
   * @returns {JSX.Element}
   */
  const renderTabContent = () => {
    switch (activeSubTab) {
      case 'regions':
        return (
          <RegionsTab
            regions={regions}
            villages={villages}
            onCreateRegion={handleCreateRegion}
            onUpdateRegion={handleUpdateRegion}
            onDeleteRegion={handleDeleteRegion}
            onCreateVillage={handleCreateVillage}
            onUpdateVillage={handleUpdateVillage}
            onDeleteVillage={handleDeleteVillage}
            onVillageSelect={handleVillageSelect}
          />
        );
      case 'village':
        return (
          <VillageTab
            village={selectedVillage}
            parcours={parcours} // Envoyer uniquement les parcours du village
            onCreateParcours={handleCreateParcours}
            onUpdateParcours={handleUpdateParcours}
            onDeleteParcours={handleDeleteParcours}
            onParcoursSelect={handleParcoursSelect}
            onTabChange={handleTabChange}
            onToggleParcoursActive={handleToggleParcoursActive}
          />
        );
      case 'parcours':
      return (
        <ParcoursTab
          village={selectedVillage}
          parcours={selectedParcours} 
          questions={questions}// Envoyer uniquement les questions du parcours
          onCreateQuestion={handleCreateQuestion}
          onUpdateQuestion={handleUpdateQuestion}
          onDeleteQuestion={handleDeleteQuestion}
          onTabChange={handleTabChange}
          onImageDelete={handleImageDelete} // 👈 ajouté
        />
      );
      case 'transient':
      return (
        <TransientTab
          onTabChange={handleTabChange}
        />
      );
      case 'users':
      return (
        <UsersTab
          onTabChange={handleTabChange}
        />
      );
      case 'independant':
      return (
        <IndependantGameTab
          onTabChange={handleTabChange}
        />
      );
      case 'statistiques':
      return (
        <Stats
          onTabChange={handleTabChange}
        />
      );
      default:
        return <ErrorTab />;
    }
  };

  // ---------------------------------LAYOUT--------------------------------------------
  return (
  <div className={styles.adminPanel}>

    {/* Onglets principaux */}
    <div className={styles.tabSelector}>
      <button 
        className={mainTab === 'jeux' ? styles.activeTab : ''} 
        onClick={() => {
          setMainTab('jeux');
          setActiveSubTab('regions');
        }}
      >Jeux</button>
      <button 
        className={mainTab === 'gestion' ? styles.activeTab : ''} 
        onClick={() => {
          setMainTab('gestion');
          setActiveSubTab('users');
        }}
      >Gestion</button>
      <button 
        className={mainTab === 'statistiques' ? styles.activeTab : ''} 
        onClick={() => {
          setMainTab('statistiques');
          setActiveSubTab('statistiques');
        }}
      >Statistiques</button>
    </div>

    {/* Sous-onglets dynamiques */}
    <div className={styles.subTabs}>
      {tabsByMain[mainTab].map(tab => (
        <button
          key={tab}
          className={activeSubTab === tab ? styles.active : ''}
          onClick={() => setActiveSubTab(tab)}
        >
          {getSubTabLabel(tab)}
        </button>
      ))}
    </div>

    {/* Contenu du sous-onglet actif */}
    <div className={styles.tabContent}>
      {renderTabContent()}
    </div>

  </div>
);

}

export default AdminPanel;
